import { Link } from "react-router-dom";
import { HeroFull } from '@/components/HeroFull';
import { HERO_IMAGE_DEFAULT } from '@/config/images';
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import { NeighborCities } from "@/components/feature/NeighborCities";
import SEO from "@/components/feature/SEO";

const PUEBLOS_AXARQUIA = [
  { name: "Comares", desc: "Pueblo blanco en la Sierra (715m). Casas tradicionales con persianas de madera conviven con nuevas urbanizaciones." },
  { name: "Macharaviaya", desc: "Pequeño municipio de tradición pesquera. Especialistas en persianas resistentes al salitre marino." },
  { name: "Frigiliana", desc: "Uno de los pueblos más bonitos de España. Restauramos persianas tradicionales en cascos históricos." },
  { name: "Cómpeta", desc: "Pueblo de la Sierra Almijara, 638m altitud. Persianas resistentes al viento de levante." },
  { name: "Canillas de Aceituno", desc: "Pueblo agrícola, conocido por sus chacinas. Persianas tradicionales de librillo." },
  { name: "Canillas de Albaida", desc: "Casco histórico bien conservado. Trabajamos persianas mallorquinas tradicionales." },
  { name: "Sayalonga", desc: "Pueblo con cementerio circular único. Persianas exteriores en viviendas blancas." },
  { name: "Árchez", desc: "Pueblo morisco con minarete. Reparación de persianas de madera y aluminio." },
  { name: "Sedella", desc: "Pueblo en la falda del Maroma. Persianas resistentes al frío invernal de la sierra." },
  { name: "Salares", desc: "Pueblo árabe perfectamente conservado. Restauración respetuosa con la arquitectura tradicional." },
  { name: "Riogordo", desc: "Conocido por su Paso bíblico. Persianas en cortijos y vivienda tradicional." },
  { name: "Periana", desc: "Famoso por el aceite de oliva y melocotón. Persianas industriales y residenciales." },
];

export default function AxarquiaHub() {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Persianas en la Axarquía | Comarca este de Málaga"
        description="Reparación e instalación de persianas, toldos y mosquiteras en toda la Axarquía: Frigiliana, Cómpeta, Comares y pueblos blancos del interior."
        canonical="/zonas/axarquia/"
        ogType="website"
        breadcrumbs={[
          { name: "Inicio", path: "/" },
          { name: "Zonas", path: "/zonas" },
          { name: "Axarquía", path: "/zonas/axarquia" },
        ]}
      />
      <Header />

      <main id="main-content">
        <Breadcrumbs
          items={[
            { name: "Inicio", path: "/" },
            { name: "Zonas", path: "/zonas" },
            { name: "Axarquía" },
          ]}
        />
        <HeroFull
          imageSrc={HERO_IMAGE_DEFAULT}
          imageAlt="Persianas Toldos Málaga atendiendo pueblos de la Axarquía"
          badge="🌄 Cobertura toda la comarca"
          title="Persianas, Toldos y Mosquiteras en la Axarquía"
          subtitle="Cubrimos toda la comarca: Vélez-Málaga, Nerja, Torrox y pueblos blancos del interior."
          primaryCta={{ href: "tel:+34951120770", label: "Llamar: 951 12 07 70" }}
          secondaryCta={{ href: "https://wa.me/34627213347", label: "💬 WhatsApp" }}
          trustSignals={["Visitas agrupadas 2x/sem", "Presupuesto gratis", "Garantía 2 años"]}
        />

        {/* Qué es la Axarquía */}
        <section className="bg-surface-alt py-12 md:py-16">
          <div className="max-w-container mx-auto px-4 md:px-6">
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-text-primary mb-4">
              ¿Qué es la Axarquía?
            </h2>
            <div className="max-w-3xl">
              <p className="font-body text-base md:text-lg text-text-secondary leading-relaxed mb-4">
                La Axarquía es la comarca oriental de la provincia de Málaga, formada por 31 municipios con una población
                total de unos 220.000 habitantes. Su capital comarcal es Vélez-Málaga y se extiende desde la costa
                (Torre del Mar, Nerja, Torrox) hasta los pueblos blancos del interior en la Sierra Tejeda y Almijara.
              </p>
              <p className="font-body text-base md:text-lg text-text-secondary leading-relaxed">
                La diversidad climática entre costa y sierra hace que las persianas en la Axarquía requieran soluciones
                distintas según la zona: aluminio resistente al salitre en la costa, persianas tradicionales o mallorquinas
                en pueblos blancos del interior.
              </p>
            </div>
          </div>
        </section>

        {/* Pueblos cubiertos */}
        <section className="bg-white py-12 md:py-16">
          <div className="max-w-container mx-auto px-4 md:px-6">
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-text-primary mb-6">
              Pueblos cubiertos en la Axarquía
            </h2>
            <p className="font-body text-base md:text-lg text-text-secondary leading-relaxed mb-8 max-w-3xl">
              Reparamos persianas en los pueblos blancos de la Axarquía interior. Visitas agrupadas 2 veces por
              semana para optimizar desplazamiento.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-5xl">
              {PUEBLOS_AXARQUIA.map((p) => (
                <div
                  key={p.name}
                  className="bg-surface-alt rounded-2xl p-6 border border-border hover:border-primary transition-colors"
                >
                  <h3 className="font-heading font-bold text-base text-text-primary mb-2">{p.name}</h3>
                  <p className="font-body text-sm text-text-secondary leading-relaxed">{p.desc}</p>
                </div>
              ))}
            </div>
            <div className="mt-8 max-w-3xl">
              <p className="font-body text-base text-text-secondary leading-relaxed">
                También cubrimos las cabeceras comarcales{" "}
                <Link to="/persianas/velez-malaga" className="text-primary hover:underline font-semibold">
                  Vélez-Málaga
                </Link>,{" "}
                <Link to="/persianas/nerja" className="text-primary hover:underline font-semibold">
                  Nerja
                </Link>{" "}
                y{" "}
                <Link to="/persianas/torrox" className="text-primary hover:underline font-semibold">
                  Torrox
                </Link>{" "}
                con city pages dedicadas y presupuesto gratuito en 24h.
              </p>
            </div>
          </div>
        </section>

        {/* Servicios */}
        <section className="bg-surface-alt py-12 md:py-16">
          <div className="max-w-container mx-auto px-4 md:px-6">
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-text-primary mb-6">
              Servicios en toda la Axarquía
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl">
              <div className="bg-white rounded-2xl p-6 border border-border">
                <h3 className="font-heading font-bold text-lg text-text-primary mb-3">Persianas</h3>
                <ul className="space-y-2 font-body text-sm text-text-secondary">
                  <li className="flex items-start gap-2"><span className="text-primary">✓</span> Reparación de motores tubulares</li>
                  <li className="flex items-start gap-2"><span className="text-primary">✓</span> Cambio de cinta o enrollador</li>
                  <li className="flex items-start gap-2"><span className="text-primary">✓</span> Sustitución de lamas (aluminio y PVC)</li>
                  <li className="flex items-start gap-2"><span className="text-primary">✓</span> Motorización de persianas manuales</li>
                </ul>
              </div>
              <div className="bg-white rounded-2xl p-6 border border-border">
                <h3 className="font-heading font-bold text-lg text-text-primary mb-3">Toldos</h3>
                <ul className="space-y-2 font-body text-sm text-text-secondary">
                  <li className="flex items-start gap-2"><span className="text-primary">✓</span> Instalación toldos extensibles</li>
                  <li className="flex items-start gap-2"><span className="text-primary">✓</span> Toldos cofre motorizados</li>
                  <li className="flex items-start gap-2"><span className="text-primary">✓</span> Reparación de brazos y lonas</li>
                  <li className="flex items-start gap-2"><span className="text-primary">✓</span> Pérgolas bioclimáticas</li>
                </ul>
              </div>
              <div className="bg-white rounded-2xl p-6 border border-border">
                <h3 className="font-heading font-bold text-lg text-text-primary mb-3">Mosquiteras</h3>
                <ul className="space-y-2 font-body text-sm text-text-secondary">
                  <li className="flex items-start gap-2"><span className="text-primary">✓</span> Mosquiteras enrollables</li>
                  <li className="flex items-start gap-2"><span className="text-primary">✓</span> Mosquiteras correderas</li>
                  <li className="flex items-start gap-2"><span className="text-primary">✓</span> Mosquiteras fijas a medida</li>
                  <li className="flex items-start gap-2"><span className="text-primary">✓</span> Cambio de tela en mosquiteras</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Tarifas */}
        <section className="bg-white py-12 md:py-16">
          <div className="max-w-container mx-auto px-4 md:px-6">
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-text-primary mb-4">
              Tarifas zona Axarquía
            </h2>
            <p className="font-body text-base md:text-lg text-text-secondary leading-relaxed mb-8 max-w-2xl">
              Precios aplicables a toda la comarca de la Axarquía. Visitas agrupadas para optimizar costes de desplazamiento.
            </p>
            <div className="max-w-2xl mx-auto bg-surface-alt rounded-3xl border border-border overflow-hidden">
              <div className="divide-y divide-border">
                <div className="flex items-center justify-between px-6 md:px-8 py-5 hover:bg-white transition-colors">
                  <span className="font-body font-medium text-text-primary">Cambiar cinta persiana</span>
                  <span className="font-heading font-bold text-lg text-primary">75€</span>
                </div>
                <div className="flex items-center justify-between px-6 md:px-8 py-5 hover:bg-white transition-colors">
                  <span className="font-body font-medium text-text-primary">Reparar motor persiana</span>
                  <span className="font-heading font-bold text-lg text-primary">95€</span>
                </div>
                <div className="flex items-center justify-between px-6 md:px-8 py-5 hover:bg-white transition-colors">
                  <span className="font-body font-medium text-text-primary">Sustituir motor</span>
                  <span className="font-heading font-bold text-lg text-primary">165€</span>
                </div>
                <div className="flex items-center justify-between px-6 md:px-8 py-5 hover:bg-white transition-colors">
                  <span className="font-body font-medium text-text-primary">Cambiar lamas (por lama)</span>
                  <span className="font-heading font-bold text-lg text-primary">15€</span>
                </div>
                <div className="flex items-center justify-between px-6 md:px-8 py-5 hover:bg-white transition-colors">
                  <span className="font-body font-medium text-text-primary">Desplazamiento</span>
                  <span className="font-heading font-bold text-lg text-primary">+10-15€</span>
                </div>
                <div className="flex items-center justify-between px-6 md:px-8 py-5 hover:bg-white transition-colors">
                  <span className="font-body font-medium text-text-primary">Revisión + presupuesto</span>
                  <span className="font-heading font-bold text-lg text-green-600">GRATIS</span>
                </div>
              </div>
              <div className="px-6 md:px-8 py-4 bg-surface-alt border-t border-border">
                <p className="font-body text-sm text-text-secondary text-center">
                  Visitas agrupadas 2 veces por semana en la Axarquía. Agrupar clientes vecinos reduce el coste de desplazamiento.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Particularidades locales (Tier 3 fallback) */}
        <section className="bg-surface-alt py-12 md:py-16">
          <div className="max-w-container mx-auto px-4 md:px-6">
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-text-primary mb-4">
              Particularidades de las persianas en pueblos serranos
            </h2>
            <p className="font-body text-base md:text-lg text-text-secondary leading-relaxed max-w-3xl">
              En los pueblos blancos de la sierra, predominan persianas de madera tradicional, mallorquinas y librillo,
              que conviven con instalaciones modernas en segundas residencias. El viento de levante y las temperaturas
              frías en invierno (incluso nieve en cotas altas) afectan especialmente a los mecanismos exteriores.
              Trabajamos tanto restauración tradicional como instalación moderna.
            </p>
          </div>
        </section>

        <NeighborCities slug="axarquia" service="persianas" />

        {/* CTA Final */}
        <section className="bg-white py-12 md:py-16">
          <div className="max-w-container mx-auto px-4 md:px-6">
            <div className="max-w-2xl mx-auto text-center">
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-text-primary mb-4">
                ¿Necesitas reparar tu persiana en la Axarquía?
              </h2>
              <p className="font-body text-lg text-text-secondary mb-8">
                Llámanos al 951 12 07 70 o pide presupuesto online. Visitamos Frigiliana, Cómpeta, Comares y todos los
                pueblos de la comarca.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a
                  href="tel:+34951120770"
                  className="inline-flex items-center gap-2 bg-primary hover:bg-primary-dark text-white font-body font-bold text-lg px-8 py-5 rounded-pill transition-colors whitespace-nowrap touch-target"
                >
                  <span aria-hidden="true">📞</span> 951 12 07 70
                </a>
                <a
                  href="https://wa.me/34627213347?text=Hola%2C%20me%20interesa%20info%20sobre%20persianas%20en%20la%20Axarquia"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-whatsapp hover:bg-[#128C7E] text-white font-body font-bold text-lg px-6 py-4 rounded-pill transition-colors whitespace-nowrap"
                >
                  <span aria-hidden="true">💬</span> WhatsApp
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}